import React, { useEffect, useMemo } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  getCountries,
  getRegions,
  getStates,
} from '../../redux/actions/regionsActions';
import { Grid } from '@material-ui/core';
import { useConfirm } from 'material-ui-confirm';

import { TreeSelectorInput } from './../../components/TreeSelector';
import { useTranslation } from 'react-i18next';

export function ParseRequestAvailabilities(items: any[]) {
  const bundle = items.reduce(
    (acc, { metas: { regionId, countryId, stateId } }) => {
      acc[regionId] = {
        ...acc[regionId],
        region: regionId,
        ...(countryId && {
          countries: {
            ...acc[regionId]?.countries,
            ...(countryId && {
              [countryId]: {
                ...acc[regionId]?.countries[countryId],
                id: countryId,
                ...(stateId && {
                  states: [
                    ...(acc[regionId]?.countries[countryId]?.states || []),
                    stateId,
                  ],
                }),
              },
            }),
          },
        }),
      };
      return acc;
    },
    {},
  );

  const array = Object.entries<any>(bundle).map(([regionId, region]) => ({
    ...region,
    ...(region.countries && {
      countries: Object.entries<any>(region.countries).map(
        ([countryId, country]) => ({
          ...country,
        }),
      ),
    }),
  }));

  return array;
}

export function GetValuesFromAvailability(values: any[] = []) {
  const parsedValue = values.reduce(
    (acc, value) => {
      const [ava, exc] = acc;
      let obj = {
        text: value.region_id || 'Global',
        path: `/${value.region_id || ''}`,
        metas: {
          regionId: value.region_id || 'GLOBAL',
          countryId: null,
          stateId: null,
        },
      };
      if (value.country) {
        obj = {
          text: value.country.description,
          path: `${obj.path}/${value.country_id}`,
          metas: {
            ...obj.metas,
            countryId: value.country_id,
          },
        };
        if (value.state) {
          obj = {
            text: value.state.description,
            path: `${obj.path}/${value.state_id}`,
            metas: {
              ...obj.metas,
              stateId: value.state_id,
            },
          };
        }
      }
      if (value.is_exclusion === 1) {
        return [ava, [...exc, obj]];
      } else {
        return [[...ava, obj], exc];
      }
    },
    [[], []],
  );

  return parsedValue;
}

export function GetSimpleAvailability(values: any[] = []) {
  return values?.map(({ metas }) => ({
    region_id: metas.regionId,
    country_id: metas.countryId,
    state_id: metas.stateId,
  }));
}

export function EquipmentAvailabilitySelector({
  allowance = [],
  exclusions = [],
  spacing = 2,
  disabled = false,
  hasExclusions = false,
  onAllowanceChange = (arg: any[]) => {},
  onExclusionsChange = (arg: any[]) => {},
}: any) {
  const dispatch: any = useDispatch();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const { regions, countriesByRegion, statesByCountry } = useSelector(
    (state: RootStateOrAny) => state.RegionsReducer,
  );

  const places = useMemo(() => {
    const rootNode = {
      type: 'Root',
      text: 'Global',
      path: '/',
      metas: {
        regionId: 'GLOBAL',
      },
      hasChildren: true,
    };
    const regionNodes = regions.map(region => {
      const regionPath = `${rootNode.path}${region.id}`;
      return {
        type: 'Region',
        text: region.name,
        path: regionPath,
        hasChildren: true,
        metas: {
          regionId: region.id,
        },
        children: countriesByRegion[region.id]?.map(country => {
          const countryPath = `${regionPath}/${country.id}`;
          return {
            type: 'Country',
            text: t(
              `app.common.countries.${country.iso3}`,
              country.description,
            ),
            path: countryPath,
            hasChildren: true,
            metas: {
              regionId: region.id,
              countryId: country.id,
            },
            children: statesByCountry[country.id]?.map(state => ({
              type: 'State',
              text: state.description,
              path: `${countryPath}/${state.id}`,
              hasChildren: false,
              metas: {
                regionId: region.id,
                countryId: country.id,
                stateId: state.id,
              },
            })),
          };
        }),
      };
    });
    return [{ ...rootNode, children: regionNodes }];
  }, [regions, countriesByRegion, statesByCountry]);

  useEffect(() => {
    if (regions?.length === 0) dispatch(getRegions());
  }, []);

  const handleLocationTreeExpanded = ({ type, metas }: any) => {
    switch (type) {
      case 'Region':
        if (!countriesByRegion[metas.regionId])
          dispatch(getCountries(metas.regionId));
        break;
      case 'Country':
        if (!statesByCountry[metas.countryId])
          dispatch(getStates(metas.regionId, metas.countryId));
        break;
      default:
        break;
    }
  };

  const handleAvailabilityChanged = (selection: any[]) => {
    onAllowanceChange(selection);
  };
  const handleExclusionsChanged = (selection: any[]) => {
    onExclusionsChange(selection);
  };

  const handleBeforeDeleteAvailability = async (node: any) => {
    let doDelete = false;
    try {
      const parentPath = node.path + (node.path.endsWith('/') ? '' : '/');
      const exclusionChildren = exclusions.filter(exclusion =>
        exclusion.path.startsWith(`${parentPath}`),
      );
      if (exclusionChildren.length > 0) {
        await confirm({
          title: `Do you want to remove availability for ${node.text}?`,
          description: `This has exception(s) for ${exclusionChildren
            .map(item => item.text)
            .join(', ')}. ${
            exclusionChildren.length > 1 ? 'They' : 'It'
          } will also be removed.`,
          confirmationText: 'Yes',
          cancellationText: 'No',
        });
        handleExclusionsChanged(
          exclusions.filter(
            exclusion => !exclusion.path.startsWith(`${parentPath}`),
          ),
        );
      }
      doDelete = true;
    } catch {}
    return doDelete;
  };

  const evalIsExclusionsNodeDisabled = (node: any) => {
    return (
      node.path === '/' ||
      !allowance?.some(allow =>
        node.path.startsWith(
          `${allow.path}${!allow.path.endsWith('/') ? '/' : ''}`,
        ),
      )
    );
  };

  return (
    <Grid container spacing={spacing}>
      <Grid item xs={hasExclusions ? 6 : 12}>
        <TreeSelectorInput
          label={t('app.common.availability')}
          nodes={places}
          checkDisabled={disabled}
          disabled={disabled}
          value={allowance || []}
          onTreeNodesExpanded={handleLocationTreeExpanded}
          onChange={handleAvailabilityChanged}
          onBeforeDelete={handleBeforeDeleteAvailability}
        />
      </Grid>
      {hasExclusions && (
        <Grid item xs={6}>
          <TreeSelectorInput
            label={t('app.common.exclusions')}
            nodes={places}
            value={exclusions || []}
            disabled={disabled}
            checkDisabled={evalIsExclusionsNodeDisabled || disabled}
            onTreeNodesExpanded={handleLocationTreeExpanded}
            onChange={handleExclusionsChanged}
          />
        </Grid>
      )}
    </Grid>
  );
}
